import { ReactNode, useEffect, useLayoutEffect, useRef } from 'react';
import * as ScrollArea from '@radix-ui/react-scroll-area';
import classNames from 'classnames';

import { NOOP } from '@lootboy/lib/utils';

import styles from './ScrollContainer.module.scss';

type TScrollContainerProps = {
  children: ReactNode;
  flex?: boolean;
  resizable?: boolean;
};

export function ScrollContainer({
  children,
  flex = false,
  resizable = false,
}: TScrollContainerProps): JSX.Element {
  const rootRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<ResizeObserver | null>(null);

  // if content is resizable (can change height dynamically) then listen to changes
  // and make sure that viewport is not larger than root element
  // e.g. new form fields appearing in contact popup
  useEffect(() => {
    // if no ResizeObserver support then do nothing
    if (!rootRef.current || !window.ResizeObserver || !resizable) {
      return NOOP;
    }

    const observer = new ResizeObserver((entries) => {
      if (!entries.length) {
        return;
      }
      const rootElementEntry = entries[0];
      const { height } = rootElementEntry.contentRect;

      if (viewportRef.current && height < viewportRef.current.clientHeight) {
        viewportRef.current.style.maxHeight = `${height}px`;
      }
    });

    observer.observe(rootRef.current);
    observerRef.current = observer;

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [resizable]);

  // for containers that don't have a height set on layout read the height of the root element
  // and force the viewport to not be larger (if content is larger than root force scrollbar)
  useLayoutEffect(() => {
    if (!rootRef.current || !viewportRef.current) {
      return;
    }
    const rootHeight = rootRef.current.clientHeight;
    if (rootHeight < viewportRef.current.clientHeight) {
      viewportRef.current.style.maxHeight = `${rootHeight}px`;
    }
  }, []);

  return (
    <ScrollArea.Root
      className={classNames(styles.root, flex && styles.flex)}
      scrollHideDelay={750}
      type="hover"
      ref={rootRef}
    >
      <ScrollArea.Viewport
        className={styles.viewport}
        ref={viewportRef}
      >
        {children}
      </ScrollArea.Viewport>
      <ScrollArea.Scrollbar
        orientation="horizontal"
        className={styles.scroll}
      >
        <ScrollArea.Thumb className={styles.thumb} />
      </ScrollArea.Scrollbar>
      <ScrollArea.Scrollbar
        orientation="vertical"
        className={styles.scroll}
      >
        <ScrollArea.Thumb className={styles.thumb} />
      </ScrollArea.Scrollbar>
      <ScrollArea.Corner />
    </ScrollArea.Root>
  );
}
